import { useCallback, useState, type FormEvent } from "react";

import { useProject } from "../app/projectContext";
import { useStudyDraft } from "../hooks/useStudyDraft";
import {
  pfCorrectionRunCreateRequestSchema,
  type PfCorrectionRunCreateRequest,
} from "../services/pfCorrection";
import type { SldElements } from "../services/sldElements";
import { describeValidationIssue, type ValidationIssueLike } from "../utils/validationMessages";
import { FaultRunSelector } from "./FaultRunSelector";
import { LoadRunSelector } from "./LoadRunSelector";
import { OtherRevisionDraftNotice } from "./OtherRevisionDraftNotice";
import { SldElementPicker, sldElementsOf } from "./SldElementPicker";
import {
  LOAD_SOURCE_CHOICE_LABELS,
  LOAD_SOURCES,
  PF_CORRECTION_DRAFT_MODULE,
  PF_CORRECTION_LABELS,
  SHORT_CIRCUIT_SOURCE_CHOICE_LABELS,
  SHORT_CIRCUIT_SOURCES,
  buildPfCorrectionRunPayload,
  createInitialPfCorrectionDraft,
  pfCorrectionDraftSchema,
  type LoadSourceChoice,
  type PfCorrectionDraft,
  type ShortCircuitSourceChoice,
} from "./pfCorrectionDraft";

// The words of the two selectors live with them; re-exported for the pages and tests.
export {
  FAULT_RUN_NOT_LINKABLE,
  NO_FAULT_RUNS_NOTE,
  NO_OPEN_REVISION_NOTE,
  faultRunOptionText,
} from "./FaultRunSelector";
export {
  LOAD_RUN_NOT_LINKABLE,
  LOAD_RUN_UNDER_REVIEW_NOTE,
  NO_LOAD_RUNS_NOTE,
  NO_OPEN_REVISION_LOAD_NOTE,
  loadRunOptionText,
} from "./LoadRunSelector";

export const TYPED_LOAD_HINT =
  "Typed figures are not traceable to a stored load study. Name the load schedule, the " +
  "measurement or the study they come from; without a source the result is Engineering " +
  "review required.";

export const TARGET_HINT =
  "No default (D3): the target follows the project's supply conditions and the tariff order " +
  "of the State Electricity Regulatory Commission. Power factors are displacement power " +
  "factors.";

export const BANK_HINT =
  "The capacitor's rated voltage and, when known, its rated kvar at that voltage; the steps " +
  "of an automatic bank as a comma-separated list, e.g. 50, 25, 12.5. No catalogue, no " +
  "manufacturer.";

export const REACTOR_HINT =
  "The detuned reactor ratio p in percent, e.g. 7 or 14; leave blank for a plain bank. The " +
  "capacitor terminal voltage with a reactor is checked against the rated voltage.";

export const SHORT_CIRCUIT_HINT =
  "The bus short-circuit power S_sc = U² / |Z_k| from the linked fault run (no voltage " +
  "factor), or a typed figure with its source; without it the resonance screening is not " +
  "made.";

export const ORDERS_HINT =
  "The characteristic harmonic orders present in the loads, as a comma-separated list, e.g. " +
  "5, 7, 11, 13 (6-pulse drives). A resonance order within ±10 % of one is flagged - a KES " +
  "screening rule, not a standard's value.";

export const NOT_MAXIMUM_SSC_NOTE =
  "This fault run is not a maximum three-phase study; the short-circuit power of a bus is " +
  "taken from its maximum three-phase fault study, so the link will be marked for review.";

/** "The fault run bus <x> differs from the study bus <y>; ..." for a power quality study. */
export function sscBusMismatchNote(faultBus: string, studyBus: string): string {
  return (
    `The fault run bus ${faultBus} differs from the study bus ${studyBus}; the short-circuit ` +
    "power is that of the study's own bus, so the link will be marked for review."
  );
}

export const PROFILE_NOTE =
  "The power factor correction study carries no profile-dependent value; the profile is " +
  "recorded with the run and must match the design basis of the project revision.";

type PfCorrectionFormProps = {
  disabled?: boolean;
  onSubmit: (payload: PfCorrectionRunCreateRequest) => void | Promise<void>;
  // The revision's single-line diagram, when it has one: the bus is picked from it and its
  // voltage fills the system voltage while that is blank.
  sldElements?: SldElements | null | undefined;
};

// The power factor correction study (EOS-11a, A32). The form owns the draft. The load
// figures start on a linked load run of the active open revision; the runs are listed from
// the load runs API and the chosen run's stored demand figures are shown read-only, for the
// engineer's eyes only - the backend reads the same stored result again when the study is
// saved. The short-circuit power starts on a linked fault run the same way. Typed values are
// allowed and said to be review required without a source.
//
// The draft is kept under its own module key, "pf-correction". The payload carries no
// project revision: the page adds the selected revision when it calls the API.
export function PfCorrectionForm({ disabled = false, onSubmit, sldElements }: PfCorrectionFormProps) {
  const { activeRevisionId } = useProject();
  const revisionId = activeRevisionId();
  const { draft, setDraft, restored, clear, otherRevision, keepForThisRevision } =
    useStudyDraft<PfCorrectionDraft>({
      module: PF_CORRECTION_DRAFT_MODULE,
      createInitial: createInitialPfCorrectionDraft,
      schema: pfCorrectionDraftSchema,
      revisionId,
    });

  const [validationError, setValidationError] = useState<string | null>(null);

  function updateStudy(patch: Partial<PfCorrectionDraft>) {
    setDraft((current) => ({ ...current, ...patch }));
  }

  // The bus picked from the diagram; its voltage fills the system voltage while that is
  // still blank, never over a value the engineer typed (A27 (f)).
  function chooseBus(busCode: string) {
    const bus = sldElementsOf(sldElements, ["bus"]).find((element) => element.code === busCode);
    setDraft((current) => ({
      ...current,
      busCode,
      systemNominalVoltageV:
        current.systemNominalVoltageV.trim() === "" && bus?.voltage_v
          ? bus.voltage_v
          : current.systemNominalVoltageV,
    }));
  }

  // Stable for the selectors' effects: each clears a run the listed revision does not hold.
  const setLoadRunId = useCallback(
    (loadRunId: string) => setDraft((current) => ({ ...current, loadRunId })),
    [setDraft],
  );
  const setFaultRunId = useCallback(
    (faultRunId: string) => setDraft((current) => ({ ...current, faultRunId })),
    [setDraft],
  );

  function clearForm() {
    setValidationError(null);
    clear();
  }

  // A selector's own sentence stands alone; every other issue is read by its label.
  function describeIssue(issue: ValidationIssueLike): string {
    const last = issue.path.at(-1);
    if (issue.code === "custom" && (last === "load_run_id" || last === "fault_run_id")) {
      return issue.message;
    }
    return describeValidationIssue(issue, PF_CORRECTION_LABELS);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setValidationError(null);

    const parsed = pfCorrectionRunCreateRequestSchema.safeParse(buildPfCorrectionRunPayload(draft));

    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setValidationError(issue ? describeIssue(issue) : "Review the power factor correction inputs.");
      return;
    }

    await onSubmit(parsed.data);
  }

  const studyText = (
    label: string,
    field: keyof PfCorrectionDraft,
    inputMode: "decimal" | "text" = "text",
  ) => (
    <label>
      {label}
      <input
        inputMode={inputMode}
        name={field}
        value={draft[field]}
        onChange={(event) => updateStudy({ [field]: event.target.value } as Partial<PfCorrectionDraft>)}
      />
    </label>
  );

  const decimal = (label: string, field: keyof PfCorrectionDraft) =>
    studyText(label, field, "decimal");

  const busCode = draft.busCode.trim();

  return (
    <form aria-label="Power factor correction inputs" onSubmit={handleSubmit}>
      {restored ? <p role="status">Draft restored from this session.</p> : null}
      {otherRevision ? (
        <OtherRevisionDraftNotice onKeep={keepForThisRevision} onClear={clearForm} disabled={disabled} />
      ) : null}

      <fieldset disabled={disabled}>
        <legend>Study definition</legend>
        {studyText("Study code", "code")}
        {studyText("Study name", "name")}
        <SldElementPicker
          label="Bus code"
          name="busCode"
          value={draft.busCode}
          onChange={chooseBus}
          categories={["bus"]}
          elements={sldElements}
        />
        {decimal("System nominal voltage (V)", "systemNominalVoltageV")}
        {decimal("Frequency (Hz)", "frequencyHz")}
        <label>
          Jurisdiction profile
          <select
            name="jurisdictionProfile"
            value={draft.jurisdictionProfile}
            onChange={(event) => updateStudy({ jurisdictionProfile: event.target.value })}
          >
            <option value="IN">India (CEA Regulations, IS, CPWD)</option>
            <option value="IEC">IEC (international, no national layer)</option>
            <option value="UK">United Kingdom (BS 7671) - reference data pending</option>
            <option value="EU">European Union (HD 60364) - reference data pending</option>
            <option value="US">United States (NEC / NFPA 70) - reference data pending</option>
            <option value="AU_NZ">
              Australia / New Zealand (AS/NZS 3000) - reference data pending
            </option>
          </select>
        </label>
        <p data-profile-note="true">{PROFILE_NOTE}</p>
        {studyText("Study notes", "notes")}
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Load figures</legend>
        <label>
          Load figures source
          <select
            name="loadSource"
            value={draft.loadSource}
            onChange={(event) => updateStudy({ loadSource: event.target.value as LoadSourceChoice })}
          >
            {LOAD_SOURCES.map((source) => (
              <option key={source} value={source}>
                {LOAD_SOURCE_CHOICE_LABELS[source]}
              </option>
            ))}
          </select>
        </label>
        {draft.loadSource === "LINKED" ? (
          <LoadRunSelector revisionId={revisionId} loadRunId={draft.loadRunId} onChange={setLoadRunId} />
        ) : (
          <>
            {decimal("Active power P (kW)", "typedActivePowerKw")}
            {decimal("Present power factor", "typedPresentPowerFactor")}
            {studyText("Load figures source text", "typedLoadSource")}
            <p data-typed-hint="true">{TYPED_LOAD_HINT}</p>
          </>
        )}
        {decimal("Target power factor", "targetPowerFactor")}
        <p data-target-hint="true">{TARGET_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Capacitor bank</legend>
        {decimal("Capacitor rated voltage (V)", "bankRatedVoltageV")}
        {decimal("Declared bank rating (kvar)", "bankDeclaredRatingKvar")}
        {studyText("Step sizes (kvar)", "stepSizesKvar")}
        <p data-bank-hint="true">{BANK_HINT}</p>
        {decimal("Detuned reactor ratio p (%)", "reactorRatioPercent")}
        <p data-reactor-hint="true">{REACTOR_HINT}</p>
      </fieldset>

      <fieldset disabled={disabled}>
        <legend>Resonance screening</legend>
        <label>
          Short-circuit power source
          <select
            name="shortCircuitSource"
            value={draft.shortCircuitSource}
            onChange={(event) =>
              updateStudy({ shortCircuitSource: event.target.value as ShortCircuitSourceChoice })
            }
          >
            {SHORT_CIRCUIT_SOURCES.map((source) => (
              <option key={source} value={source}>
                {SHORT_CIRCUIT_SOURCE_CHOICE_LABELS[source]}
              </option>
            ))}
          </select>
        </label>
        {draft.shortCircuitSource === "LINKED" ? (
          <FaultRunSelector
            revisionId={revisionId}
            faultRunId={draft.faultRunId}
            onChange={setFaultRunId}
            busCode={busCode}
            withPeak={false}
            notMaximumNote={NOT_MAXIMUM_SSC_NOTE}
            busMismatchNoteFor={sscBusMismatchNote}
          />
        ) : draft.shortCircuitSource === "TYPED" ? (
          <>
            {decimal("Short-circuit power S_sc (kVA)", "typedShortCircuitPowerKva")}
            {studyText("Short-circuit power source text", "typedShortCircuitSource")}
          </>
        ) : null}
        <p data-short-circuit-hint="true">{SHORT_CIRCUIT_HINT}</p>
        {studyText("Characteristic harmonic orders", "characteristicOrders")}
        <p data-orders-hint="true">{ORDERS_HINT}</p>
      </fieldset>

      {validationError ? <p role="alert">{validationError}</p> : null}

      <button disabled={disabled} type="submit">
        Size the compensation
      </button>
      <button disabled={disabled} type="button" onClick={clearForm}>
        Clear form
      </button>
    </form>
  );
}

export default PfCorrectionForm;
